
import { useFormContext } from 'react-hook-form';
import { useState, useEffect } from 'react';
import { makeStyles } from 'tss-react/mui';
import clsx from 'clsx';
import { AxiosError } from 'axios';
import { getAttorneyFeeEntityIdentifier, updateFirmDisposition } from '../../../../api/paymentsApi/settlementsApi';
import { setFirmExternalVendorId, removeFirmExternalVendorId } from '../../../../api/firmsApi/firmsApi';
import { setVendorExternalVendorId, removeExternalVendorId } from '../../../../api/vendorApi/vendorAccountsApi';
import ConfirmationPopup from '../../../../components/ConfirmationPopup';
import FindNetSuiteVendorPopup from '../../../../components/FindNetSuiteVendorPopup';
import useSnackBar from '../../../../hooks/useSnackBar';
import { ErrorData } from '../../../../types';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Theme,
    Typography,
    Box,
    Tooltip,
    TooltipProps,
    Button,
    useTheme,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import LinkIcon from '@mui/icons-material/Link';
import LaunchIcon from '@mui/icons-material/Launch';
import SearchIcon from '@mui/icons-material/Search';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import StorefrontIcon from '@mui/icons-material/Storefront';
import BusinessIcon from '@mui/icons-material/Business';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { numberToUSDCurrency } from '../../../../helpers/numberToUSDCurrency';
import TablePaginationLocalContainer from '../../../../components/Table/TablePaginationLocalContainer';
import TruncatedText from '../../../../components/TruncatedText';
import { FIRM_ROLES } from '../../../CasesView/types';
import FormSelectField from '../../../../components/formFields/FormSelectField';
import { INTENT_TYPE_OPTIONS, ROWS_PER_PAGE_OPTIONS, NETSUITE_LABEL } from '../../../../constants/constants';
import ViewCommonButton from '../../../../components/buttons/ViewCommonButton';
import { SettlementAttorneyFeePopup } from '../SettlementAttorneyFeePopup';
import { accountingVendorType } from '../../../VendorsView/helpers';

const useStyles = makeStyles<{ isLongData?: boolean }>()((theme: Theme, { isLongData }) => ({
    accordion: {
        backgroundColor: 'transparent',
        marginTop: theme.spacing(1),
        boxShadow: 'none',
        overflow: 'hidden',
        fontFamily: 'Poppins',
    },
    accordionSummary: {
        backgroundColor: theme.palette.primary.light,
        minHeight: '56px',
        padding: '0 16px',
        fontFamily: 'Poppins',
        '& .MuiTypography-root': {
            fontWeight: 600,
            fontSize: '16px',
            color: theme.palette.common.white,
            fontFamily: 'Poppins',
        },
        flexDirection: 'row-reverse'

    },
    accordionDetails: {
        padding: theme.spacing(2),
        backgroundColor: theme.palette.primary.light,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
        fontFamily: 'Poppins',
    },
    tableContainer: {
        width: '99%',
        background: 'transparent',
        border: `1px solid ${theme.palette.secondary.dark}`,
        borderRadius: '8px',
        overflow: 'auto',
        fontSize: '14px',
        lineHeight: '17px',
        fontWeight: 500,
        fontFamily: 'Poppins',
        minHeight: isLongData ? '300px' : 'inherit',
        '&::-webkit-scrollbar': {
            width: '5px',
            height: '5px',
            borderRadius: '20px',
        },
        '&::-webkit-scrollbar-thumb': {
            borderRadius: 20,
            backgroundColor: theme.palette.secondary.dark,
        },
    },
    tableHeaderCell: {
        background: theme.palette.primary.light,
        '& .MuiTableCell-root': {
            textAlign: 'left',
            color: theme.palette.text.secondary,
            borderBottom: '1px solid #515151',
            fontWeight: 500,
            fontFamily: 'Poppins',
            fontSize: '14px',
            height: '35px',
            padding: '16px 12px 16px 8px',
            whiteSpace: 'nowrap',
            minWidth: '120px',
        },
    },
    tableBodyCell: {
        '& .MuiTableCell-root': {
            textAlign: 'left',
            color: theme.palette.common.white,
            borderBottom: '1px solid #4A4E5D',
            fontWeight: 400,
            fontFamily: 'Poppins',
            fontSize: '14px',
            height: '35px',
            padding: '16px 12px 16px 8px',
            minWidth: '120px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
    },
    labelText: {
        fontSize: '14px',
        fontWeight: 500,
        fontFamily: 'Poppins',
        color: '#FFFFFFB3',
    },
}));

const entityTooltipProps: TooltipProps['componentsProps'] = {
    tooltip: {
        sx: (theme) => ({
            backgroundColor: theme.palette.common.white,
            color: theme.palette.primary.light,
            borderRadius: '5px',
            boxShadow: '3px',
            fontFamily: 'Inter',
            fontSize: '13px',
            fontWeight: 500,
            padding: '5px',
            maxWidth: '140px',
        }),
    },
};

export interface AttorneyFeeRowKey {
    id: number;
    counselEntityType?: 'FIRM' | 'VENDOR' | null;
    firmId?: number | null;
    vendorFirmParticipationId?: number | null;
}

/** Rows must be matched on entity type plus id: a firm id and a vendor id can be the same number. */
export const isSameAttorneyFee = (
    a?: Partial<AttorneyFeeRowKey> | null,
    b?: Partial<AttorneyFeeRowKey> | null,
) =>
    !!a && !!b && a.id === b.id && (a.counselEntityType ?? 'FIRM') === (b.counselEntityType ?? 'FIRM');

interface AttorneyFeesTableProps {
    totalAttorneyFees: number;
    settlementId?: number;
    onDispositionChange?: () => void;
    canEdit?: boolean;
    isOnboardingReadOnly?: boolean;
    handleMenuOptionClick: (option: string, row: AttorneyFeeRowKey) => void;
    onSyncStateChange?: (syncedMap: Record<number, boolean>) => void;
    onAttorneyFeeUpdate?: (updatedAttorneyFee: {
        id: number;
        counselEntityType: 'FIRM' | 'VENDOR';
        firmId: number | null;
        vendorFirmParticipationId: number | null;
        name: string;
        disposition: 'Withhold' | 'Pay' | 'External';
        fee: number;
        externalPaymentDate: string | null;
        externalPaymentMemo: string | null;
        externalPaymentReference: string | null;
        paymentMethod: string;
        role: string;
    }) => void;
    caseId: number;
    onSuccess?: () => void;
    sequenceNumber?: number;
}

const AttorneyFeesTable = ({ totalAttorneyFees, settlementId, onDispositionChange, canEdit, isOnboardingReadOnly, handleMenuOptionClick, onSyncStateChange, onAttorneyFeeUpdate, onSuccess, sequenceNumber }: AttorneyFeesTableProps) => {
    const { watch, setValue } = useFormContext();
    const { setAlert } = useSnackBar();
    const theme = useTheme();
    const attorneyFeesFields = watch('attorneysFees');
    const isLongData = attorneyFeesFields?.length > 3;
    const styles = useStyles({ isLongData });
    const hasAccounting = Array.isArray(attorneyFeesFields) && attorneyFeesFields.some((f: any) => f?.accounting != null); // eslint-disable-line @typescript-eslint/no-explicit-any

    const vendorType = accountingVendorType;

    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);
    const paginatedFields = Array.isArray(attorneyFeesFields)
        ? attorneyFeesFields.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        : [];

    // Per-firm sync state
    const [syncedMap, setSyncedMap] = useState<Record<number, boolean>>({});
    const [linkMap, setLinkMap] = useState<Record<number, string | null>>({});
    const [isPopupOpen, setIsPopupOpen] = useState(false);


    useEffect(() => {
        if (!Array.isArray(attorneyFeesFields) || attorneyFeesFields.length === 0) return;

        const syncMap: Record<number, boolean> = {};
        const lMap: Record<number, string | null> = {};

        (attorneyFeesFields as any[]).forEach((f) => { // eslint-disable-line @typescript-eslint/no-explicit-any
            if (f?.id != null) {
                syncMap[f.id] = f.accounting?.isSynced ?? false;
                lMap[f.id] = f.accounting?.link ?? null;
            }
        });

        setSyncedMap(syncMap);
        setLinkMap(lMap);
    }, [attorneyFeesFields]);

    const [activeFirmId, setActiveFirmId] = useState<number | null>(null);
    const [activeFirmName, setActiveFirmName] = useState('');
    const [activeEntityType, setActiveEntityType] = useState<'FIRM' | 'VENDOR'>('FIRM');
    const [activeApiId, setActiveApiId] = useState<number | null>(null);
    const [syncAction, setSyncAction] = useState<'sync' | 'relink'>('sync');
    const [isNetSuitePopupOpen, setIsNetSuitePopupOpen] = useState(false);
    const [isUnlinkConfirmOpen, setIsUnlinkConfirmOpen] = useState(false);

    const selectActiveEntity = (field: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
        const isVendor = field.role !== 'LeadCounsel' && field.counselEntityType === 'VENDOR';
        setActiveFirmId(field.id);
        setActiveFirmName(field.name);
        setActiveEntityType(isVendor ? 'VENDOR' : 'FIRM');
        setActiveApiId(isVendor ? (field.vendorFirmParticipationId ?? field.id) : field.id);
    };

    const openNetSuitePopup = (field: any, action: 'sync' | 'relink') => { // eslint-disable-line @typescript-eslint/no-explicit-any
        selectActiveEntity(field);
        setSyncAction(action);
        setIsNetSuitePopupOpen(true);
    };

    const entityLabel = activeEntityType === 'VENDOR' ? 'Vendor' : 'Firm';

    // Sync/relink directly from the review popup, no confirmation step
    const handleNetSuiteVendorSelect = async (internalId: string) => {
        if (!activeFirmId || !activeApiId || !internalId) return;
        try {
            const response = activeEntityType === 'VENDOR'
                ? await setVendorExternalVendorId(activeApiId, internalId)
                : await setFirmExternalVendorId(activeApiId, internalId);
            setAlert(() => ({
                message: (response.data as { message?: string }).message || `${entityLabel} ${syncAction === 'sync' ? 'synced' : 'relinked'} successfully.`,
                type: 'success',
            }));
            const newIsSynced = response.data.isSynced ?? false;
            setSyncedMap((prev) => {
                const next = { ...prev, [activeFirmId]: newIsSynced };
                onSyncStateChange?.(next);
                return next;
            });
            setLinkMap((prev) => ({ ...prev, [activeFirmId]: response.data.link ?? null }));
            setIsNetSuitePopupOpen(false);
            if (onDispositionChange) onDispositionChange();
        } catch (error) {
            const axiosError = error as AxiosError<ErrorData>;
            setAlert(() => ({
                message: axiosError.response?.data?.message || `Failed to ${syncAction} ${entityLabel.toLowerCase()}.`,
                type: 'error',
            }));
        }
    };

    const handleConfirmUnlink = async () => {
        if (!activeFirmId || !activeApiId) return;
        try {
            const response = activeEntityType === 'VENDOR'
                ? await removeExternalVendorId(activeApiId)
                : await removeFirmExternalVendorId(activeApiId);
            setAlert(() => ({
                message: response.data.message || `${entityLabel} unlinked successfully.`,
                type: 'success',
            }));
            setSyncedMap((prev) => {
                const next = { ...prev, [activeFirmId]: false };
                onSyncStateChange?.(next);
                return next;
            });
            setLinkMap((prev) => ({ ...prev, [activeFirmId]: null }));
            setIsUnlinkConfirmOpen(false);
            if (onDispositionChange) onDispositionChange();
        } catch (error) {
            const axiosError = error as AxiosError<ErrorData>;
            setAlert(() => ({
                message: axiosError.response?.data?.message || `Failed to unlink ${entityLabel.toLowerCase()}.`,
                type: 'error',
            }));
        }
    };

    return (
        <>
            <Accordion defaultExpanded className={styles.classes.accordion}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />} className={styles.classes.accordionSummary}>
                    {/* <Typography>Attorney Fees</Typography> */}
                    <Box display='flex' alignItems='center' width='100%' justifyContent='space-between'>
                        <Box display='flex' alignItems='center' gap='10px'>
                            <Typography>Attorney Fees</Typography>
                            <Box
                                onClick={(e) => e.stopPropagation()}
                                sx={{
                                    '& .MuiBox-root': {
                                        padding: '7px 15px !important',
                                    },
                                    '& .MuiTypography-root': { fontSize: '14px !important' },
                                }}
                            >
                                <ViewCommonButton type="editFeeConfiguration" onClick={() => setIsPopupOpen(true)} />
                            </Box>
                        </Box>
                        <Typography className={styles.classes.labelText}>{numberToUSDCurrency(totalAttorneyFees)}</Typography>

                    </Box>
                </AccordionSummary>
                <AccordionDetails className={styles.classes.accordionDetails}>
                    <TableContainer className={styles.classes.tableContainer} component={Paper}>
                        <Table sx={{ minWidth: 430 }} size="small">
                            <TableHead className={styles.classes.tableHeaderCell}>
                                <TableRow>
                                    <TableCell key={'name'} sx={hasAccounting ? { width: '27%' } : {}}>{'Name'}</TableCell>
                                    <TableCell key={'amount'} sx={hasAccounting ? { width: '14%' } : {}}>{'Amount'}</TableCell>
                                    <TableCell key={'disposition'} sx={hasAccounting ? { width: '17%' } : {}}>{'Intent'}</TableCell>
                                    <TableCell key={'paymentMethod'} sx={hasAccounting ? { width: '17%' } : {}}>{'Payment Method'}</TableCell>
                                    <TableCell key={'Action'} sx={hasAccounting ? { width: '25%' } : {}}>{'Action'}</TableCell>

                                </TableRow>
                            </TableHead>
                            <TableBody className={clsx(styles.classes.tableBodyCell)}>
                                {paginatedFields.length > 0 ?
                                    (
                                        paginatedFields.map((field: any, idx: number) => ( // eslint-disable-line @typescript-eslint/no-explicit-any
                                            <TableRow key={`${field.id}-${idx}`}>
                                                <TableCell sx={{ maxWidth: '220px' }}>
                                                    <Box display="flex" alignItems="center" gap="8px" overflow="hidden">
                                                        {/* Lead firm is always a firm; secondary rows follow counselEntityType */}
                                                        {field.role !== 'LeadCounsel' && field.counselEntityType === 'VENDOR' ? (
                                                            <Tooltip title="Vendor" arrow componentsProps={entityTooltipProps}>
                                                                <StorefrontIcon sx={{ fontSize: '20px', color: 'common.white', flexShrink: 0 }} />
                                                            </Tooltip>
                                                        ) : (
                                                            <Tooltip title="Firm" arrow componentsProps={entityTooltipProps}>
                                                                <BusinessIcon sx={{ fontSize: '20px', color: 'common.white', flexShrink: 0 }} />
                                                            </Tooltip>
                                                        )}
                                                        <TruncatedText text={field.name || '-'} maxLength={30} />
                                                        {linkMap[field.id] != null && (
                                                            <Tooltip
                                                                title={field.role !== 'LeadCounsel' && field.counselEntityType === 'VENDOR' ? `View Vendor in ${NETSUITE_LABEL}` : `View Firm in ${NETSUITE_LABEL}`}
                                                                arrow
                                                                componentsProps={{
                                                                    tooltip: {
                                                                        sx: (theme) => ({
                                                                            backgroundColor: theme.palette.common.white,
                                                                            color: theme.palette.primary.light,
                                                                            borderRadius: '5px',
                                                                            boxShadow: '3px',
                                                                            fontFamily: 'Inter',
                                                                            fontSize: '13px',
                                                                            fontWeight: 500,
                                                                            padding: '5px',
                                                                            maxWidth: '140px',
                                                                        }),
                                                                    },
                                                                }}
                                                            >
                                                                <LaunchIcon
                                                                    sx={{
                                                                        cursor: 'pointer',
                                                                        color: 'common.white',
                                                                        fontSize: '20px',
                                                                        flexShrink: 0,
                                                                    }}
                                                                    onClick={() => window.open(linkMap[field.id] || '', '_blank')}
                                                                />
                                                            </Tooltip>
                                                        )}
                                                        {field.accounting && (
                                                            <Box
                                                                sx={{
                                                                    backgroundColor: syncedMap[field.id] ? 'success.main' : 'warning.main',
                                                                    color: 'white',
                                                                    fontSize: '10px',
                                                                    padding: '2px 6px',
                                                                    borderRadius: '8px',
                                                                    whiteSpace: 'nowrap',
                                                                    flexShrink: 0,
                                                                }}
                                                            >
                                                                {syncedMap[field.id] ? 'Synced' : 'Unsynced'}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                    {field.role && (
                                                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', fontSize: '12px' }}>
                                                            {FIRM_ROLES[field.role as keyof typeof FIRM_ROLES]}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {field.fee || '-'}
                                                </TableCell>
                                                <TableCell>
                                                    {/* {field.disposition || '-'} */}
                                                    <Tooltip
                                                        title={
                                                            !canEdit && isOnboardingReadOnly
                                                                ? 'This is disabled. See the details page for more information.'
                                                                : ''
                                                        }
                                                        arrow
                                                    >
                                                        <Box component="span" sx={{ display: 'inline-flex', width: '100%' }}>
                                                            <FormSelectField
                                                                name={`attorneysFees[${idx}].disposition`}
                                                                label="Intent"
                                                                options={INTENT_TYPE_OPTIONS}
                                                                width="70%"
                                                                disabled={!canEdit}
                                                                customOnChange={(value: string) => {
                                                                    const dispositionFieldName = `attorneysFees[${idx}].disposition`;

                                                                    const oldDisposition = watch(dispositionFieldName);

                                                                    // Payment method is left untouched until the API responds,
                                                                    // so it doesn't flash a guessed value before the real one.
                                                                    setValue(dispositionFieldName, value, { shouldValidate: false });

                                                                    if (!settlementId) {
                                                                        setValue(dispositionFieldName, oldDisposition, { shouldValidate: false });
                                                                        return;
                                                                    }

                                                                    const payload = {
                                                                        ...getAttorneyFeeEntityIdentifier(field),
                                                                        disposition: value as 'Withhold' | 'Pay' | 'External',
                                                                    };

                                                                    updateFirmDisposition(settlementId, payload)
                                                                        .then((res) => {
                                                                            if (!res.data.error) {
                                                                                const updatedAttorneyFee = res.data.data || res.data;

                                                                                setValue(`attorneysFees[${idx}].disposition`, updatedAttorneyFee.disposition, {
                                                                                    shouldValidate: false,
                                                                                });

                                                                                setValue(`attorneysFees[${idx}].paymentMethod`, updatedAttorneyFee.paymentMethod ?? (updatedAttorneyFee.disposition === 'External' ? '-' : 'V-Firm'), {
                                                                                    shouldValidate: false,
                                                                                });

                                                                                setValue(
                                                                                    `attorneysFees[${idx}].externalPaymentDate`,
                                                                                    updatedAttorneyFee.externalPaymentDate,
                                                                                    {
                                                                                        shouldValidate: false,
                                                                                    },
                                                                                );

                                                                                setValue(
                                                                                    `attorneysFees[${idx}].externalPaymentMemo`,
                                                                                    updatedAttorneyFee.externalPaymentMemo,
                                                                                    {
                                                                                        shouldValidate: false,
                                                                                    },
                                                                                );

                                                                                setValue(
                                                                                    `attorneysFees[${idx}].externalPaymentReference`,
                                                                                    updatedAttorneyFee.externalPaymentReference,
                                                                                    {
                                                                                        shouldValidate: false,
                                                                                    },
                                                                                );

                                                                                onAttorneyFeeUpdate?.(updatedAttorneyFee);

                                                                                setAlert((prev) => ({
                                                                                    ...prev,
                                                                                    message: res.data.message || 'Attorney fee updated successfully.',
                                                                                    type: 'success',
                                                                                }));

                                                                                onDispositionChange?.();
                                                                            }
                                                                        })
                                                                        .catch((error: AxiosError<ErrorData>) => {
                                                                            setValue(dispositionFieldName, oldDisposition, { shouldValidate: false });

                                                                            setAlert({
                                                                                type: 'error',
                                                                                message: error.response?.data?.message || 'Failed to update disposition',
                                                                            });
                                                                        });
                                                                }}
                                                            />
                                                        </Box>
                                                    </Tooltip>

                                                </TableCell>
                                                <TableCell>
                                                    {field.paymentMethod || '-'}

                                                </TableCell>
                                                <TableCell>
                                                    <Box display="flex" alignItems="center" gap="8px" flexWrap="nowrap">
                                                        <Tooltip
                                                            title={
                                                                !canEdit && isOnboardingReadOnly
                                                                    ? 'This is disabled. See the details page for more information.'
                                                                    : 'Edit'
                                                            }
                                                            arrow
                                                            componentsProps={{
                                                                tooltip: {
                                                                    sx: (theme) => ({
                                                                        backgroundColor: theme.palette.common.white,
                                                                        color: theme.palette.primary.light,
                                                                        borderRadius: '5px',
                                                                        boxShadow: '3px',
                                                                        fontFamily: 'Inter',
                                                                        fontSize: '13px',
                                                                        fontWeight: 500,
                                                                        padding: '5px',
                                                                        maxWidth: '140px',
                                                                    }),
                                                                },
                                                            }}>
                                                            <span style={{ display: 'inline-flex' }}>
                                                                <EditIcon
                                                                    sx={{
                                                                        cursor: canEdit ? 'pointer' : 'not-allowed',
                                                                        color: 'common.white',
                                                                        opacity: canEdit ? 1 : 0.5,
                                                                    }}
                                                                    onClick={canEdit ? () => handleMenuOptionClick('Edit', field) : undefined}
                                                                    fontSize='small'
                                                                />
                                                            </span>
                                                        </Tooltip>
                                                        {field.accounting && !syncedMap[field.id] && (
                                                            <Button
                                                                variant="contained"
                                                                startIcon={<SearchIcon sx={{ fontSize: '16px' }} />}
                                                                onClick={() => openNetSuitePopup(field, 'sync')}
                                                                sx={{
                                                                    backgroundColor: theme.palette.info.main,
                                                                    color: 'white',
                                                                    textTransform: 'none',
                                                                    fontSize: '14px',
                                                                    fontWeight: 500,
                                                                    padding: '8px 8px',
                                                                    width: '184px',
                                                                    borderRadius: '12px',
                                                                    whiteSpace: 'nowrap',
                                                                    '&:hover': { backgroundColor: theme.palette.info.dark },
                                                                }}
                                                            >
                                                                Match firm
                                                            </Button>
                                                        )}
                                                        {field.accounting && syncedMap[field.id] && (
                                                            <>
                                                                <Button
                                                                    variant="contained"
                                                                    startIcon={<LinkIcon sx={{ fontSize: '16px' }} />}
                                                                    onClick={() => openNetSuitePopup(field, 'relink')}
                                                                    sx={{
                                                                        backgroundColor: theme.palette.info.main,
                                                                        color: 'white',
                                                                        textTransform: 'none',
                                                                        fontSize: '14px',
                                                                        fontWeight: 500,
                                                                        padding: '8px 8px',
                                                                        width: '88px',
                                                                        borderRadius: '12px',
                                                                        whiteSpace: 'nowrap',
                                                                        '&:hover': { backgroundColor: theme.palette.info.dark },
                                                                    }}
                                                                >
                                                                    Relink
                                                                </Button>
                                                                <Button
                                                                    variant="outlined"
                                                                    startIcon={<LinkOffIcon sx={{ fontSize: '16px' }} />}
                                                                    onClick={() => {
                                                                        selectActiveEntity(field);
                                                                        setIsUnlinkConfirmOpen(true);
                                                                    }}
                                                                    sx={{
                                                                        borderColor: theme.palette.error.main,
                                                                        color: theme.palette.error.main,
                                                                        textTransform: 'none',
                                                                        fontSize: '14px',
                                                                        fontWeight: 500,
                                                                        padding: '8px 8px',
                                                                        width: '88px',
                                                                        borderRadius: '12px',
                                                                        whiteSpace: 'nowrap',
                                                                        '&:hover': {
                                                                            borderColor: theme.palette.error.dark,
                                                                            backgroundColor: 'rgba(211, 47, 47, 0.08)',
                                                                        },
                                                                    }}
                                                                >
                                                                    Unlink
                                                                </Button>
                                                            </>
                                                        )}
                                                    </Box>
                                                </TableCell>

                                            </TableRow>
                                        ))
                                    ) :
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ textAlign: 'center !important' }}>
                                            <Typography variant="body2" color="textSecondary">
                                                No Attorney Fee records found
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePaginationLocalContainer
                        count={Array.isArray(attorneyFeesFields) ? attorneyFeesFields.length : 0}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        onRowsPerPageChange={e => {
                            setRowsPerPage(parseInt(e.target.value as string, ROWS_PER_PAGE_OPTIONS[0])); setPage(0);
                        }}
                        totalPages={Array.isArray(attorneyFeesFields) ? Math.ceil(attorneyFeesFields.length / rowsPerPage) : 0}
                    />
                </AccordionDetails>
            </Accordion>

            <FindNetSuiteVendorPopup
                open={isNetSuitePopupOpen}
                lienName={activeFirmName}
                searchTerm={activeFirmName}
                entityType="Firm"
                entityLabel="Firm"
                entityId={activeFirmId ?? undefined}
                onClose={() => setIsNetSuitePopupOpen(false)}
                onVendorSelectFromNetsuite={handleNetSuiteVendorSelect}
                syncAction={syncAction}
            />

            <ConfirmationPopup
                open={isUnlinkConfirmOpen}
                title="Confirm Unlink"
                message={`Are you sure you want to unlink this Firm ${activeFirmName} from ${vendorType}? This action will remove the connection between V.Law and ${vendorType} for this firm.`}
                confirmText="Unlink"
                onConfirm={handleConfirmUnlink}
                onCancel={() => setIsUnlinkConfirmOpen(false)}
            />
            <SettlementAttorneyFeePopup
                isOpen={isPopupOpen}
                onClose={() => setIsPopupOpen(false)}
                settlementId={Number(settlementId)}
                isReadOnly={(sequenceNumber ?? 1) > 1}
                onSave={() => {
                    onSuccess?.();
                }}
            />
        </>
    );
};

export default AttorneyFeesTable;